/**
 * matchBrowserMenuController — P6-12 menu Crear / Unirse / Sandbox.
 * DOM injected via `els` for unit tests (no document required).
 */

import { ROOM_VISIBILITY } from '../../domain/net/wsProtocol.js';
import {
  buildCreateRoomPayload,
  renderRoomListHtml,
  showPanel,
  hidePanel,
  populateMatchBrowserMaps
} from './matchBrowserMenuView.js';

export {
  buildCreateRoomPayload,
  renderRoomListHtml
} from './matchBrowserMenuView.js';

function setStatus(els, text) {
  if (els.statusEl) els.statusEl.textContent = text;
}

function showOnly(els, key) {
  hidePanel(els.menuPanel);
  hidePanel(els.createPanel);
  hidePanel(els.joinPanel);
  hidePanel(els.roomReadyPanel);
  showPanel(els[key]);
}

function syncPasswordVisibility(els) {
  const privateMode = els.visibilitySelect?.value === ROOM_VISIBILITY.PRIVATE;
  if (els.passwordGroup) els.passwordGroup.hidden = !privateMode;
}

function enterRoomReady(els, room, onRoomReady, client) {
  showOnly(els, 'roomReadyPanel');
  const n = room?.players?.length || 0;
  if (els.roomReadyLabel) {
    els.roomReadyLabel.textContent =
      `Sala ${room.roomId} · ${n} jugador(es) · mapa ${room.mapId}`;
  }
  setStatus(els, `En sala ${room.roomId}`);
  if (typeof onRoomReady === 'function') {
    onRoomReady({ room, client });
  }
}

/**
 * @param {{
 *   els: object,
 *   maps?: string[],
 *   createClient: () => object,
 *   onSandbox: () => void,
 *   onRoomReady: (info: { room: object, client: object }) => void
 * }} opts
 */
export function createMatchBrowserMenuController(opts) {
  const { els, maps, createClient, onSandbox, onRoomReady } = opts;
  let client = null;

  async function ensureClient() {
    if (client) return client;
    client = createClient();
    await client.connect();
    return client;
  }

  function goMenu() {
    showOnly(els, 'menuPanel');
    setStatus(els, '');
  }

  function goCreate() {
    showOnly(els, 'createPanel');
    syncPasswordVisibility(els);
  }

  function goJoin() {
    showOnly(els, 'joinPanel');
  }

  function chooseSandbox() {
    if (els.root) {
      els.root.hidden = true;
      if (els.root.style) els.root.style.display = 'none';
    }
    if (typeof onSandbox === 'function') onSandbox();
  }

  async function submitCreate() {
    try {
      const payload = buildCreateRoomPayload({
        mapId: els.mapSelect?.value,
        visibility: els.visibilitySelect?.value,
        password: els.passwordInput?.value
      });
      setStatus(els, 'Creando sala…');
      const c = await ensureClient();
      const room = await c.createRoom(payload);
      enterRoomReady(els, room, onRoomReady, c);
      return room;
    } catch (err) {
      setStatus(els, `Error: ${err.message}`);
      throw err;
    }
  }

  async function refreshRoomList() {
    try {
      setStatus(els, 'Actualizando lista…');
      const c = await ensureClient();
      const rooms = await c.listRooms();
      if (els.roomListEl) {
        els.roomListEl.innerHTML = renderRoomListHtml(rooms);
        bindRoomListClicks();
      }
      setStatus(els, rooms.length ? `${rooms.length} partida(s)` : 'Sin partidas públicas');
      return rooms;
    } catch (err) {
      setStatus(els, `Error: ${err.message}`);
      throw err;
    }
  }

  function bindRoomListClicks() {
    const root = els.roomListEl;
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('[data-room-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-room-id');
        joinListed(id).catch(() => {});
      });
    });
  }

  async function joinListed(roomId) {
    try {
      setStatus(els, `Uniéndose a ${roomId}…`);
      const c = await ensureClient();
      const room = await c.joinRoom({ roomId });
      enterRoomReady(els, room, onRoomReady, c);
      return room;
    } catch (err) {
      setStatus(els, `Error: ${err.message}`);
      throw err;
    }
  }

  async function joinPrivate() {
    const roomId = String(els.privateRoomIdInput?.value || '').trim();
    const password = String(els.privatePasswordInput?.value || '');
    if (!roomId) {
      setStatus(els, 'Error: room id requerido');
      throw new Error('room_id_required');
    }
    try {
      setStatus(els, `Uniéndose a ${roomId}…`);
      const c = await ensureClient();
      const room = await c.joinRoom({ roomId, password });
      enterRoomReady(els, room, onRoomReady, c);
      return room;
    } catch (err) {
      setStatus(els, `Error: ${err.message}`);
      throw err;
    }
  }

  populateMatchBrowserMaps(els.mapSelect, maps);
  syncPasswordVisibility(els);
  showOnly(els, 'menuPanel');

  if (els.btnCreate) els.btnCreate.addEventListener('click', () => goCreate());
  if (els.btnJoin) {
    els.btnJoin.addEventListener('click', () => {
      goJoin();
      refreshRoomList().catch(() => {});
    });
  }
  if (els.btnSandbox) els.btnSandbox.addEventListener('click', () => chooseSandbox());
  if (els.btnCreateBack) els.btnCreateBack.addEventListener('click', () => goMenu());
  if (els.btnJoinBack) els.btnJoinBack.addEventListener('click', () => goMenu());
  if (els.btnCreateSubmit) {
    els.btnCreateSubmit.addEventListener('click', () => {
      submitCreate().catch(() => {});
    });
  }
  if (els.btnRefreshList) {
    els.btnRefreshList.addEventListener('click', () => {
      refreshRoomList().catch(() => {});
    });
  }
  if (els.btnJoinPrivate) {
    els.btnJoinPrivate.addEventListener('click', () => {
      joinPrivate().catch(() => {});
    });
  }
  if (els.visibilitySelect) {
    els.visibilitySelect.onchange = (e) => {
      const t = e?.target || els.visibilitySelect;
      els.visibilitySelect.value = t.value;
      syncPasswordVisibility(els);
    };
    if (typeof els.visibilitySelect.addEventListener === 'function') {
      els.visibilitySelect.addEventListener('change', () => syncPasswordVisibility(els));
    }
  }

  return {
    goMenu,
    goCreate,
    goJoin,
    chooseSandbox,
    submitCreate,
    refreshRoomList,
    joinListed,
    joinPrivate,
    getClient: () => client
  };
}

/** Bind real DOM ids under #match-browser-overlay. */
export function bindMatchBrowserMenuFromDocument(doc, opts = {}) {
  const root = doc.getElementById('match-browser-overlay');
  if (!root) return null;
  const els = {
    root,
    menuPanel: doc.getElementById('mb-panel-menu'),
    createPanel: doc.getElementById('mb-panel-create'),
    joinPanel: doc.getElementById('mb-panel-join'),
    roomReadyPanel: doc.getElementById('mb-panel-room'),
    statusEl: doc.getElementById('mb-status'),
    mapSelect: doc.getElementById('mb-map-select'),
    visibilitySelect: doc.getElementById('mb-visibility-select'),
    passwordGroup: doc.getElementById('mb-password-group'),
    passwordInput: doc.getElementById('mb-password-input'),
    roomListEl: doc.getElementById('mb-room-list'),
    privateRoomIdInput: doc.getElementById('mb-private-room-id'),
    privatePasswordInput: doc.getElementById('mb-private-password'),
    btnCreate: doc.getElementById('mb-btn-create'),
    btnJoin: doc.getElementById('mb-btn-join'),
    btnSandbox: doc.getElementById('mb-btn-sandbox'),
    btnCreateBack: doc.getElementById('mb-btn-create-back'),
    btnCreateSubmit: doc.getElementById('mb-btn-create-submit'),
    btnJoinBack: doc.getElementById('mb-btn-join-back'),
    btnRefreshList: doc.getElementById('mb-btn-refresh'),
    btnJoinPrivate: doc.getElementById('mb-btn-join-private'),
    roomReadyLabel: doc.getElementById('mb-room-ready-label')
  };
  return createMatchBrowserMenuController({ ...opts, els });
}
